import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  isLoading?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  onPageChange?: (page: number) => void;
  /** Если передан — рендерим селектор размера страницы и кнопки jump. */
  onPageSizeChange?: (size: number) => void;
  /** Доступные размеры страницы. По умолчанию [25, 50, 100]. */
  pageSizeOptions?: number[];
  emptyMessage?: string;
}

export function DataTable<T>({
  columns,
  data,
  total,
  page,
  pageSize,
  isLoading,
  sorting,
  onSortingChange,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  emptyMessage,
}: DataTableProps<T>): JSX.Element {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [jumpInput, setJumpInput] = useState<string>("");

  // Сбрасываем jump-input когда меняется текущая страница
  useEffect(() => {
    setJumpInput("");
  }, [page]);

  function jumpTo(raw: string) {
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 1 && n <= totalPages) {
      onPageChange?.(n);
    }
    setJumpInput("");
  }

  const table = useReactTable({
    data,
    columns,
    state: { sorting: sorting ?? [] },
    onSortingChange: (updater) => {
      if (onSortingChange) {
        const newState =
          typeof updater === "function" ? updater(sorting ?? []) : updater;
        onSortingChange(newState);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: totalPages,
  });

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: pageSize }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_c, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage ?? "Ничего не найдено"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination row */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-3 text-muted-foreground">
          {total > 0 && (
            <span>
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} из {total}
            </span>
          )}
          {onPageSizeChange && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs">На странице:</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  const n = parseInt(v, 10);
                  if (Number.isFinite(n) && n > 0) onPageSizeChange(n);
                }}
              >
                <SelectTrigger className="h-7 w-[68px] px-2 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((opt) => (
                    <SelectItem key={opt} value={String(opt)}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(1)}
            disabled={page <= 1}
            title="Первая страница"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {totalPages > 1 && (
            <div className="flex items-center gap-1 px-1.5">
              {totalPages > 10 ? (
                <>
                  <Input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={jumpInput || page}
                    onChange={(e) => setJumpInput(e.target.value)}
                    onBlur={(e) => {
                      if (e.target.value !== String(page)) jumpTo(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        jumpTo((e.target as HTMLInputElement).value);
                      }
                    }}
                    className="h-7 w-14 px-2 text-center text-xs"
                  />
                  <span className="text-muted-foreground">/ {totalPages}</span>
                </>
              ) : (
                <span className="px-1">
                  {page} / {totalPages}
                </span>
              )}
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(page + 1)}
            disabled={page >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(totalPages)}
            disabled={page >= totalPages}
            title="Последняя страница"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
